import type { ContentOptimizationErrorTextKey } from "./types";
import { extractContentBlockNames } from "./extractContentBlockNames";

/**
 * Augments the message texts used for CO validation with the bodies of referenced content blocks.
 *
 * Each block's HTML is appended only to the error-text key(s) whose strings actually reference
 * that block (`body`, `sending_info`, and/or `generic`), so unknown-component and related errors
 * stay attributed to the right composer area.
 *
 * Returns the original object unchanged when there is nothing to append (no bodies, only empty
 * bodies, or no `{{content_blocks.${...}}}` references that match fetched bodies).
 */
export const augmentMessageTextsWithContentBlockBodies = (
  messageTexts: Partial<Record<ContentOptimizationErrorTextKey, string[]>>,
  contentBlockBodies: Record<string, string>
): Partial<Record<ContentOptimizationErrorTextKey, string[]>> => {
  const bodyTexts = messageTexts.body ?? [];
  const sendingInfoTexts = messageTexts.sending_info ?? [];
  const genericTexts = messageTexts.generic ?? [];
  const namesInBody = extractContentBlockNames(...bodyTexts);
  const namesInSendingInfo = extractContentBlockNames(...sendingInfoTexts);
  const namesInGeneric = extractContentBlockNames(...genericTexts);

  const bodyAdditions: string[] = [];
  const sendingInfoAdditions: string[] = [];
  const genericAdditions: string[] = [];

  for (const [blockName, html] of Object.entries(contentBlockBodies)) {
    if (!html || html.length === 0) {
      continue;
    }
    if (namesInBody.has(blockName)) {
      bodyAdditions.push(html);
    }
    if (namesInSendingInfo.has(blockName)) {
      sendingInfoAdditions.push(html);
    }
    if (namesInGeneric.has(blockName)) {
      genericAdditions.push(html);
    }
  }

  if (bodyAdditions.length === 0 && sendingInfoAdditions.length === 0 && genericAdditions.length === 0) {
    return messageTexts;
  }

  return {
    ...messageTexts,
    ...(bodyAdditions.length > 0 ? { body: [...bodyTexts, ...bodyAdditions] } : {}),
    ...(sendingInfoAdditions.length > 0 ? { sending_info: [...sendingInfoTexts, ...sendingInfoAdditions] } : {}),
    ...(genericAdditions.length > 0 ? { generic: [...genericTexts, ...genericAdditions] } : {}),
  };
};
